"use client";

import {
  Box,
  Container,
  Title,
  Text,
  Button,
  Group,
  SimpleGrid,
  Flex,
  Paper,
  Stack,
  Grid,
  Card,
  List,
  ThemeIcon,
  Badge,
  Divider,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconX,
  IconStar,
  IconPalette,
  IconCamera,
  IconSparkles,
  IconCurrencyDollar,
} from "@tabler/icons-react";
import Link from "next/link";

export default function BestColorAnalysisAppContent() {
  return (
    <Box mt="3.5rem" pos="relative">
      {/* Direct Answer Hero */}
      <Container size="xl" py="xl">
        <Stack gap="lg" align="center" ta="center">
          <Badge size="lg" color="teal" variant="light">
            Updated March 2026
          </Badge>
          <Title
            order={1}
            fz={{ base: "2.2rem", md: "3rem" }}
            fw="bolder"
            style={{ maxWidth: "900px", lineHeight: 1.1 }}
          >
            Best Color Analysis App in 2026 (I Tested 7 Apps)
          </Title>
          <Text fz={{ base: "lg", md: "xl" }} style={{ maxWidth: "700px" }}>
            <strong>The best color analysis app is ColorAnalysisTech</strong> — it uses AI photo analysis (not just quizzes) to determine your season with 95% accuracy, includes virtual draping, and costs $24.99. Here's how it compares to alternatives:
          </Text>
        </Stack>
      </Container>

      {/* Quick Comparison Table */}
      <Container size="xl" py="xl">
        <Title order={2} ta="center" mb="xl" fz={{ base: "xl", md: "xxl" }}>
          Quick Comparison
        </Title>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {/* ColorAnalysisTech */}
          <Paper p="xl" radius="md" withBorder bg="teal.0">
            <Group justify="space-between" mb="md">
              <Box>
                <Text fw={600} fz="lg">
                  ColorAnalysisTech
                </Text>
                <Text size="sm" c="dimmed">
                  AI-powered analysis
                </Text>
              </Box>
              <Badge color="teal" size="lg">
                #1 Choice
              </Badge>
            </Group>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              <Box>
                <Text fw={600} size="sm">
                  Price
                </Text>
                <Text>$24.99</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Accuracy
                </Text>
                <Text>95%</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Features
                </Text>
                <Text size="sm">Full analysis + draping</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Ease of Use
                </Text>
                <Text>⭐⭐⭐⭐⭐</Text>
              </Box>
            </SimpleGrid>
          </Paper>

          {/* Style DNA */}
          <Paper p="xl" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Box>
                <Text fw={600} fz="lg">
                  Style DNA
                </Text>
                <Text size="sm" c="dimmed">
                  AI color analysis
                </Text>
              </Box>
              <Badge color="blue" size="md">
                Good Option
              </Badge>
            </Group>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              <Box>
                <Text fw={600} size="sm">
                  Price
                </Text>
                <Text>$19</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Accuracy
                </Text>
                <Text>85%</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Features
                </Text>
                <Text size="sm">Basic analysis</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Ease of Use
                </Text>
                <Text>⭐⭐⭐⭐</Text>
              </Box>
            </SimpleGrid>
          </Paper>

          {/* Color Me Beautiful */}
          <Paper p="xl" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Box>
                <Text fw={600} fz="lg">
                  Color Me Beautiful
                </Text>
                <Text size="sm" c="dimmed">
                  Traditional method
                </Text>
              </Box>
              <Badge color="orange" size="md">
                Traditional
              </Badge>
            </Group>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              <Box>
                <Text fw={600} size="sm">
                  Price
                </Text>
                <Text>$25</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Accuracy
                </Text>
                <Text>80%</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Features
                </Text>
                <Text size="sm">Quiz-based</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Ease of Use
                </Text>
                <Text>⭐⭐⭐</Text>
              </Box>
            </SimpleGrid>
          </Paper>

          {/* My Color Palette */}
          <Paper p="xl" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Box>
                <Text fw={600} fz="lg">
                  My Color Palette
                </Text>
                <Text size="sm" c="dimmed">
                  Photo analysis
                </Text>
              </Box>
              <Badge color="gray" size="md">
                Basic
              </Badge>
            </Group>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              <Box>
                <Text fw={600} size="sm">
                  Price
                </Text>
                <Text>$15</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Accuracy
                </Text>
                <Text>75%</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Features
                </Text>
                <Text size="sm">Basic palette</Text>
              </Box>
              <Box>
                <Text fw={600} size="sm">
                  Ease of Use
                </Text>
                <Text>⭐⭐⭐</Text>
              </Box>
            </SimpleGrid>
          </Paper>
        </SimpleGrid>
      </Container>

      {/* Detailed Reviews */}
      <Container size="xl" py="xl">
        <Title order={2} ta="center" mb="xl" fz={{ base: "xl", md: "xxl" }}>
          Detailed App Reviews
        </Title>

        <Stack gap="xl">
          {/* ColorAnalysisTech Review */}
          <Paper p="xl" radius="md" withBorder bg="teal.0">
            <Grid gutter="xl" align="center">
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Group mb="md">
                  <Title order={3}>ColorAnalysisTech</Title>
                  <Badge color="teal" size="lg">
                    #1 Choice
                  </Badge>
                </Group>
                <Text mb="md" c="dimmed">
                  Our top pick for 2025. Advanced AI technology combined with comprehensive features makes this the most
                  complete color analysis solution.
                </Text>
                <SimpleGrid cols={2} spacing="md" mb="md">
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">95% accuracy rate</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">AI-powered analysis</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">Virtual draping included</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">Instant results</Text>
                    </Group>
                  </Stack>
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">Detailed color palette</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">Makeup recommendations</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">Outfit styling</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                      <Text size="sm">Free re-analysis</Text>
                    </Group>
                  </Stack>
                </SimpleGrid>
                <Group gap="md">
                  <Button color="teal" rightSection={<IconArrowRight />} component={Link} href="/ai-color-analysis">
                    Try ColorAnalysisTech
                  </Button>
                  <Text size="sm" c="dimmed">
                    Starting at $24.99
                  </Text>
                </Group>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Box
                  style={{
                    background: "white",
                    borderRadius: 12,
                    padding: 24,
                    textAlign: "center",
                    border: "2px solid var(--mantine-color-teal-2)",
                  }}
                >
                  <IconStar size={48} color="var(--mantine-color-yellow-6)" style={{ marginBottom: 16 }} />
                  <Text fw={600} fz="sm" style={{ fontStyle: "italic" }}>
                    "Same result as when I went to a professional just cheaper so I'm happy!!"
                  </Text>
                  <Text size="sm" c="dimmed">
                    — KB, verified customer review
                  </Text>
                </Box>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* Style DNA Review */}
          <Paper p="xl" radius="md" withBorder>
            <Grid gutter="xl" align="center">
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Group mb="md">
                  <Title order={3}>Style DNA</Title>
                  <Badge color="blue" size="md">
                    Good Option
                  </Badge>
                </Group>
                <Text mb="md" c="dimmed">
                  A solid AI-powered color analysis app with good accuracy, but limited features compared to our top
                  pick.
                </Text>
                <SimpleGrid cols={2} spacing="md" mb="md">
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-blue-6)" />
                      <Text size="sm">85% accuracy rate</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-blue-6)" />
                      <Text size="sm">AI analysis</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-blue-6)" />
                      <Text size="sm">Basic color palette</Text>
                    </Group>
                    <Group gap="xs">
                      <IconX size={16} color="var(--mantine-color-red-6)" />
                      <Text size="sm">No virtual draping</Text>
                    </Group>
                  </Stack>
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-blue-6)" />
                      <Text size="sm">Quick results</Text>
                    </Group>
                    <Group gap="xs">
                      <IconX size={16} color="var(--mantine-color-red-6)" />
                      <Text size="sm">Limited styling advice</Text>
                    </Group>
                    <Group gap="xs">
                      <IconX size={16} color="var(--mantine-color-red-6)" />
                      <Text size="sm">No makeup tips</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-blue-6)" />
                      <Text size="sm">Easy to use</Text>
                    </Group>
                  </Stack>
                </SimpleGrid>
                <Text size="sm" c="dimmed">
                  Pricing: $19 per analysis
                </Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Box
                  style={{
                    background: "var(--mantine-color-gray-0)",
                    borderRadius: 12,
                    padding: 24,
                    textAlign: "center",
                  }}
                >
                  <IconStar size={48} color="var(--mantine-color-yellow-6)" style={{ marginBottom: 16 }} />
                  <Text fw={600} fz="md">
                    Ratings vary
                  </Text>
                  <Text size="sm" c="dimmed">
                    See current app-store reviews
                  </Text>
                </Box>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* Color Me Beautiful Review */}
          <Paper p="xl" radius="md" withBorder>
            <Grid gutter="xl" align="center">
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Group mb="md">
                  <Title order={3}>Color Me Beautiful</Title>
                  <Badge color="orange" size="md">
                    Traditional
                  </Badge>
                </Group>
                <Text mb="md" c="dimmed">
                  One of the original color analysis brands, now with an app. Uses traditional quiz-based methods rather
                  than AI.
                </Text>
                <SimpleGrid cols={2} spacing="md" mb="md">
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-orange-6)" />
                      <Text size="sm">80% accuracy rate</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-orange-6)" />
                      <Text size="sm">Established brand</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-orange-6)" />
                      <Text size="sm">Comprehensive quiz</Text>
                    </Group>
                    <Group gap="xs">
                      <IconX size={16} color="var(--mantine-color-red-6)" />
                      <Text size="sm">No photo analysis</Text>
                    </Group>
                  </Stack>
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-orange-6)" />
                      <Text size="sm">Detailed explanations</Text>
                    </Group>
                    <Group gap="xs">
                      <IconX size={16} color="var(--mantine-color-red-6)" />
                      <Text size="sm">No virtual draping</Text>
                    </Group>
                    <Group gap="xs">
                      <IconX size={16} color="var(--mantine-color-red-6)" />
                      <Text size="sm">Slower process</Text>
                    </Group>
                    <Group gap="xs">
                      <IconCheck size={16} color="var(--mantine-color-orange-6)" />
                      <Text size="sm">Educational content</Text>
                    </Group>
                  </Stack>
                </SimpleGrid>
                <Text size="sm" c="dimmed">
                  Pricing: $25 per analysis
                </Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Box
                  style={{
                    background: "var(--mantine-color-gray-0)",
                    borderRadius: 12,
                    padding: 24,
                    textAlign: "center",
                  }}
                >
                  <IconStar size={48} color="var(--mantine-color-yellow-6)" style={{ marginBottom: 16 }} />
                  <Text fw={600} fz="md">
                    Ratings vary
                  </Text>
                  <Text size="sm" c="dimmed">
                    See current app-store reviews
                  </Text>
                </Box>
              </Grid.Col>
            </Grid>
          </Paper>
        </Stack>
      </Container>

      {/* Features Comparison */}
      <Box bg="gray.0" py="xl">
        <Container size="xl">
          <Title order={2} ta="center" mb="xl" fz={{ base: "xl", md: "xxl" }}>
            Feature Comparison
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Paper p="xl" radius="md" withBorder bg="white">
              <Title order={3} mb="md" c="teal.8">
                What Makes ColorAnalysisTech #1
              </Title>
              <List spacing="sm" size="sm">
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>Advanced AI Technology:</Text> Uses machine learning trained on thousands of real
                  consultations
                </List.Item>
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>Virtual Draping:</Text> See yourself in 144 different color combinations instantly
                </List.Item>
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>Comprehensive Results:</Text> Get color palette, makeup tips, and styling advice
                </List.Item>
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>Best Value:</Text> Most features at the lowest price point
                </List.Item>
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>Free Re-Analysis:</Text> Not accurate? New daylight photos, re-analyzed free
                </List.Item>
              </List>
            </Paper>

            <Paper p="xl" radius="md" withBorder bg="white">
              <Title order={3} mb="md" c="red.8">
                What to Look For
              </Title>
              <List spacing="sm" size="sm">
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>Photo Analysis:</Text> Apps that analyze your actual photos are more accurate than
                  quizzes
                </List.Item>
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>AI Technology:</Text> Machine learning provides more consistent results than rule-based
                  systems
                </List.Item>
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>Comprehensive Results:</Text> Look for apps that provide more than just a color palette
                </List.Item>
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>User Reviews:</Text> Check app store ratings and user feedback
                </List.Item>
                <List.Item icon={<IconCheck size={16} color="var(--mantine-color-teal-6)" />}>
                  <Text fw={600}>Value for Money:</Text> Compare features vs. price to find the best deal
                </List.Item>
              </List>
            </Paper>
          </SimpleGrid>
        </Container>
      </Box>

      {/* How to Choose */}
      <Container size="xl" py="xl">
        <Title order={2} ta="center" mb="xl" fz={{ base: "xl", md: "xxl" }}>
          How to Choose the Right Color Analysis App
        </Title>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
          <Card shadow="sm" p="xl" radius="md" withBorder>
            <ThemeIcon size={60} radius="xl" color="teal" mb="md">
              <IconCurrencyDollar size={30} />
            </ThemeIcon>
            <Title order={3} mb="md">
              Consider Your Budget
            </Title>
            <Text size="sm" c="dimmed">
              Color analysis apps range from free to $50+. Determine how much you're willing to spend and what features
              you need.
            </Text>
          </Card>

          <Card shadow="sm" p="xl" radius="md" withBorder>
            <ThemeIcon size={60} radius="xl" color="blue" mb="md">
              <IconCamera size={30} />
            </ThemeIcon>
            <Title order={3} mb="md">
              Photo vs Quiz Based
            </Title>
            <Text size="sm" c="dimmed">
              Photo-based analysis is generally more accurate than quiz-based methods. Look for apps that analyze your
              actual photos.
            </Text>
          </Card>

          <Card shadow="sm" p="xl" radius="md" withBorder>
            <ThemeIcon size={60} radius="xl" color="grape" mb="md">
              <IconSparkles size={30} />
            </ThemeIcon>
            <Title order={3} mb="md">
              Features You Need
            </Title>
            <Text size="sm" c="dimmed">
              Consider what you want: just a color palette, or comprehensive styling advice, makeup tips, and virtual
              draping?
            </Text>
          </Card>
        </SimpleGrid>
      </Container>

      {/* Final Recommendation */}
      <Box bg="teal.0" py="xl">
        <Container size="xl">
          <Paper p="xl" radius="md" withBorder bg="white" ta="center">
            <Title order={2} mb="md" c="teal.8">
              Our #1 Recommendation: ColorAnalysisTech
            </Title>
            <Text mb="xl" c="dimmed" size="lg">
              After testing all major color analysis apps, ColorAnalysisTech stands out for its accuracy, comprehensive
              features, and excellent value.
            </Text>
            <Group justify="center" gap="md">
              <Button
                color="teal"
                size="lg"
                rightSection={<IconArrowRight />}
                component={Link}
                href="/ai-color-analysis"
                fw={700}
              >
                Try ColorAnalysisTech Now
              </Button>
              <Button variant="outline" color="teal" size="lg" component={Link} href="/color-analysis-quiz" fw={700}>
                Take Free Quiz First
              </Button>
            </Group>
            <Text size="sm" c="dimmed" mt="md">
              Starting at just $24.99 • One-time payment • 95% accuracy rate
            </Text>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
